import React from 'react';
import { Check, X } from 'lucide-react';
import { cropRect, fitWithin, type Pt, type Rect, type Shot } from '@/lib/feedback/core';

/* SPEC-506 — the snip step. The page capture fills the screen; drag a box round the part
 * that matters, or take the whole page in one tap. The crop is cut in the capture's natural
 * pixels and downscaled to the upload bound before it ever leaves the browser. */

export async function cropToShot(capture: Shot, rect: Rect | null, maxPx: number): Promise<Shot> {
  const img = await loadImage(capture.url);
  const r = rect ?? { x: 0, y: 0, w: img.naturalWidth, h: img.naturalHeight };
  const out = fitWithin(r.w, r.h, maxPx);
  const canvas = document.createElement('canvas');
  canvas.width = out.w;
  canvas.height = out.h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas unavailable');
  // JPEG has no transparency: paint white first, or a transparent paste turns black.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out.w, out.h);
  ctx.drawImage(img, r.x, r.y, r.w, r.h, 0, 0, out.w, out.h);
  const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', 0.85));
  if (!blob) throw new Error('could not encode the snip');
  return { url: URL.createObjectURL(blob), width: out.w, height: out.h, source: capture.source };
}

export function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image failed to load'));
    img.src = url;
  });
}

export function SnipOverlay({ capture, maxPx, onDone, onSkip }: {
  capture: Shot;
  maxPx: number;
  onDone: (shot: Shot) => void;
  onSkip: () => void;
}) {
  const imgRef = React.useRef<HTMLImageElement>(null);
  const [start, setStart] = React.useState<Pt | null>(null);
  const [end, setEnd] = React.useState<Pt | null>(null);
  const [busy, setBusy] = React.useState(false);

  // Display size: the capture fitted inside the viewport, leaving room for the bar.
  const display = React.useMemo(() => {
    const maxW = window.innerWidth - 48;
    const maxH = window.innerHeight - 120;
    const k = Math.min(1, maxW / capture.width, maxH / capture.height);
    return { w: Math.round(capture.width * k), h: Math.round(capture.height * k) };
  }, [capture.width, capture.height]);

  const local = (e: React.PointerEvent): Pt => {
    const box = imgRef.current!.getBoundingClientRect();
    return { x: e.clientX - box.left, y: e.clientY - box.top };
  };

  const finish = async (useWhole: boolean) => {
    setBusy(true);
    const rect = useWhole || !start || !end
      ? null
      : cropRect(start, end, display.w, display.h, capture.width, capture.height);
    try {
      onDone(await cropToShot(capture, rect, maxPx));
    } catch {
      onSkip();
    }
  };

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onSkip();
      if (e.key === 'Enter') void finish(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const sel = start && end ? {
    left: Math.min(start.x, end.x), top: Math.min(start.y, end.y),
    width: Math.abs(end.x - start.x), height: Math.abs(end.y - start.y),
  } : null;
  const hasArea = !!(start && end && cropRect(start, end, display.w, display.h, capture.width, capture.height));

  return (
    <div data-feedback-ui="" role="dialog" aria-modal="true" aria-label="Choose the part of the page to send"
      className="fixed inset-0 z-[70] bg-black/80 flex flex-col items-center justify-center gap-3 p-6">
      <div className="flex flex-wrap items-center justify-center gap-2 text-sm">
        <span className="text-white mr-2">Drag a box round the part that matters — or use the whole page.</span>
        <button type="button" disabled={busy} onClick={() => void finish(true)}
          className="px-3 py-1.5 rounded-md bg-amber-500 text-[var(--text-inverse)] font-medium hover:bg-amber-400 disabled:opacity-60">
          Use whole page
        </button>
        <button type="button" disabled={busy || !hasArea} onClick={() => void finish(false)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-white/40 text-white hover:bg-white/10 disabled:opacity-40">
          <Check className="w-4 h-4" aria-hidden="true" /> Use this area
        </button>
        <button type="button" onClick={onSkip}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-gray-200 hover:text-white hover:bg-white/10">
          <X className="w-4 h-4" aria-hidden="true" /> No screenshot
        </button>
      </div>
      <div className="relative overflow-hidden rounded-md select-none touch-none" style={{ width: display.w, height: display.h }}
        onPointerDown={(e) => { (e.target as Element).setPointerCapture?.(e.pointerId); const p = local(e); setStart(p); setEnd(p); }}
        onPointerMove={(e) => { if (start && e.buttons) setEnd(local(e)); }}
      >
        <img ref={imgRef} src={capture.url} alt="A picture of the page you were on" draggable={false}
          className="block w-full h-full rounded-md shadow-2xl cursor-crosshair" />
        {sel && sel.width > 0 && (
          <div className="absolute border-2 border-amber-400 bg-amber-400/10 pointer-events-none"
            style={{ ...sel, boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)' }} />
        )}
      </div>
    </div>
  );
}
